import { useEffect, useMemo, useRef, useState } from 'react'
import { geoIdentity, geoPath } from 'd3-geo'
import type { Feature, FeatureCollection, Geometry } from 'geojson'
import {
  AlertTriangle,
  Map as MapIcon,
  RotateCcw,
  ZoomIn,
  ZoomOut,
  type LucideIcon,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'

const GEOJSON_URL = '/geo/peru-departamentos.geojson'

const VIEWBOX_W = 280
const VIEWBOX_H = 320
const PADDING = 10
const CLIP_ID = 'mapa-peru-clip'

const ZOOM_MIN = 1
const ZOOM_MAX = 12
const ZOOM_STEP = 1.4

// Tonos de verde para el gradiente
const COLOR_SHADES = [
  '#bfdbfe', // blue-200
  '#93c5fd', // blue-300
  '#60a5fa', // blue-400
  '#3b82f6', // blue-500
  '#2563eb', // blue-600
  '#1d4ed8', // blue-700
  '#1e40af', // blue-800
]

export const PURPLE_SHADES = ['#ede9fc','#d4cff0','#b8b0e0','#9d93cf','#8d82bc','#7468a8','#5b5090']
export const GREEN_SHADES  = ['#bbf7d0','#86efac','#4ade80','#22c55e','#16a34a','#15803d','#166534']

interface DeptProps {
  shapeName: string
}

type DeptFeature = Feature<Geometry, DeptProps>
type DeptsCollection = FeatureCollection<Geometry, DeptProps>

export interface MapaDatum {
  label: string
  value: number | string
}

interface Props {
  title: string
  data: MapaDatum[]
  isLoading?: boolean
  error?: unknown
  icon?: LucideIcon
  formatValue?: (v: number) => string
  headerStat?: { value: number | string; label: string }
  colorShades?: string[]
}

function normalizeKey(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim()
}

function parseNumeric(value: unknown): number {
  if (value === null || value === undefined || value === '') return 0
  if (typeof value === 'number') return value
  const n = parseFloat(String(value))
  return Number.isNaN(n) ? 0 : n
}

function useGeoJSON() {
  const [data, setData] = useState<DeptsCollection | null>(null)
  const [error, setError] = useState<unknown>(null)
  useEffect(() => {
    let active = true
    fetch(GEOJSON_URL)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.json() as Promise<DeptsCollection>
      })
      .then((j) => { if (active) setData(j) })
      .catch((e) => { if (active) setError(e) })
    return () => { active = false }
  }, [])
  return { data, error }
}

function buildValueMap(data: MapaDatum[]): { values: Map<string, number>; max: number } {
  const values = new Map<string, number>()
  let max = 0
  for (const d of data) {
    const key = normalizeKey(d.label)
    const n = parseNumeric(d.value)
    values.set(key, (values.get(key) ?? 0) + n)
  }
  for (const v of values.values()) if (v > max) max = v
  return { values, max }
}

function fillFor(value: number, max: number, shades: string[] = COLOR_SHADES): string {
  if (max <= 0 || value <= 0) return 'hsl(var(--muted))'
  const ratio = Math.sqrt(value / max)
  const idx = Math.min(shades.length - 1, Math.max(0, Math.round(ratio * (shades.length - 1))))
  return shades[idx]
}

interface Transform { k: number; x: number; y: number }
const IDENTITY: Transform = { k: 1, x: 0, y: 0 }

function clampTransform(t: Transform): Transform {
  const k = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, t.k))
  return { k, x: Math.max(-(k - 1) * VIEWBOX_W, Math.min(0, t.x)), y: Math.max(-(k - 1) * VIEWBOX_H, Math.min(0, t.y)) }
}

function zoomAt(t: Transform, factor: number, px: number, py: number): Transform {
  const newK = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, t.k * factor))
  const ratio = newK / t.k
  return clampTransform({ k: newK, x: px - (px - t.x) * ratio, y: py - (py - t.y) * ratio })
}

interface TooltipState { x: number; y: number; nombre: string; valor: number; visible: boolean }

export function MapaDepartamentosPeru({
  title,
  data,
  isLoading,
  error,
  icon: Icon = MapIcon,
  formatValue,
  headerStat,
  colorShades,
}: Props) {
  const { data: geo, error: geoError } = useGeoJSON()
  const svgRef = useRef<SVGSVGElement>(null)
  const dragRef = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null)
  const [transform, setTransform] = useState<Transform>(IDENTITY)
  const [tooltip, setTooltip] = useState<TooltipState>({ x: 0, y: 0, nombre: '', valor: 0, visible: false })

  const activeShades = colorShades ?? COLOR_SHADES

  const { values, max } = useMemo(() => buildValueMap(data), [data])

  const pathGen = useMemo(() => {
    if (!geo) return null
    const proj = geoIdentity().reflectY(true).fitExtent(
      [[PADDING, PADDING], [VIEWBOX_W - PADDING, VIEWBOX_H - PADDING]],
      geo,
    )
    return geoPath(proj)
  }, [geo])

  const fmt = (v: number) => formatValue ? formatValue(v) : v.toLocaleString('es-PE')

  useEffect(() => {
    const svg = svgRef.current
    if (!svg) return
    const handler = (e: WheelEvent) => {
      e.preventDefault()
      const rect = svg.getBoundingClientRect()
      const px = ((e.clientX - rect.left) / rect.width) * VIEWBOX_W
      const py = ((e.clientY - rect.top) / rect.height) * VIEWBOX_H
      const factor = e.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP
      setTransform((t) => zoomAt(t, factor, px, py))
    }
    svg.addEventListener('wheel', handler, { passive: false })
    return () => svg.removeEventListener('wheel', handler)
  }, [])

  const onMouseDown = (e: React.MouseEvent<SVGSVGElement>) => {
    if (e.button !== 0) return
    dragRef.current = { x: e.clientX, y: e.clientY, ox: transform.x, oy: transform.y }
  }

  const onMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const drag = dragRef.current
    const svg = svgRef.current
    if (!drag || !svg) return
    const rect = svg.getBoundingClientRect()
    const dx = (e.clientX - drag.x) * (VIEWBOX_W / rect.width)
    const dy = (e.clientY - drag.y) * (VIEWBOX_H / rect.height)
    setTransform((t) => clampTransform({ k: t.k, x: drag.ox + dx, y: drag.oy + dy }))
  }

  const onMouseUp = () => { dragRef.current = null }
  const zoomBy = (factor: number) => setTransform((t) => zoomAt(t, factor, VIEWBOX_W / 2, VIEWBOX_H / 2))
  const reset = () => setTransform(IDENTITY)

  const renderHeader = (extra?: React.ReactNode) => (
    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-muted-foreground" />
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        {headerStat && (
          <span className="ml-2 inline-flex items-baseline gap-1 rounded-md border border-gray-200 dark:border-gray-700 bg-muted/40 px-2 py-0.5 text-xs">
            <span className="font-semibold text-black dark:text-white">{headerStat.value}</span>
            <span className="text-muted-foreground">{headerStat.label}</span>
          </span>
        )}
      </div>
      {extra}
    </CardHeader>
  )

  if (isLoading || (!geo && !geoError && !error)) {
    return (
      <Card className="border border-gray-300 dark:border-gray-700">
        {renderHeader()}
        <CardContent><Skeleton className="h-[280px] w-full" /></CardContent>
      </Card>
    )
  }

  if (error || geoError) {
    return (
      <Card className="border border-gray-300 dark:border-gray-700">
        {renderHeader()}
        <CardContent>
          <div className="flex flex-col items-center justify-center gap-2 h-[280px] text-center px-4">
            <AlertTriangle className="h-6 w-6 text-amber-500" />
            <p className="text-sm font-medium">Error al cargar el mapa</p>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (!pathGen || !geo) {
    return (
      <Card className="border border-gray-300 dark:border-gray-700">
        {renderHeader()}
        <CardContent>
          <div className="flex items-center justify-center h-[280px] text-muted-foreground text-sm">Sin datos disponibles</div>
        </CardContent>
      </Card>
    )
  }

  const onDeptEnter = (e: React.MouseEvent<SVGPathElement>, feature: DeptFeature) => {
    const svg = svgRef.current
    if (!svg) return
    const rect = svg.getBoundingClientRect()
    const valor = values.get(normalizeKey(feature.properties.shapeName)) ?? 0
    setTooltip({ x: e.clientX - rect.left, y: e.clientY - rect.top, nombre: feature.properties.shapeName, valor, visible: true })
  }

  const onDeptLeave = () => setTooltip((t) => ({ ...t, visible: false }))

  return (
    <Card className="border border-gray-300 dark:border-gray-700 h-full">
      {renderHeader(
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => zoomBy(ZOOM_STEP)} aria-label="Acercar">
            <ZoomIn className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => zoomBy(1 / ZOOM_STEP)} aria-label="Alejar">
            <ZoomOut className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={reset} aria-label="Restablecer">
            <RotateCcw className="h-4 w-4" />
          </Button>
        </div>,
      )}
      <CardContent>
        <div className="relative w-full" style={{ aspectRatio: `${VIEWBOX_W} / ${VIEWBOX_H}`, maxHeight: 320 }}>
          <svg
            ref={svgRef}
            viewBox={`0 0 ${VIEWBOX_W} ${VIEWBOX_H}`}
            className="absolute inset-0 h-full w-full select-none touch-none"
            style={{ cursor: dragRef.current ? 'grabbing' : 'grab' }}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={() => { onMouseUp(); onDeptLeave() }}
            role="img"
            aria-label={title}
          >
            <defs>
              <clipPath id={CLIP_ID}>
                <rect x={0} y={0} width={VIEWBOX_W} height={VIEWBOX_H} />
              </clipPath>
            </defs>
            <g clipPath={`url(#${CLIP_ID})`}>
              <g transform={`translate(${transform.x} ${transform.y}) scale(${transform.k})`}>
                {geo.features.map((f) => {
                  const feature = f as DeptFeature
                  const d = pathGen(feature)
                  if (!d) return null
                  const valor = values.get(normalizeKey(feature.properties.shapeName)) ?? 0
                  const fill = fillFor(valor, max, activeShades)
                  return (
                    <path
                      key={feature.properties.shapeName}
                      d={d}
                      fill={fill}
                      strokeWidth={0.5 / transform.k}
                      vectorEffect="non-scaling-stroke"
                      className="stroke-gray-400 dark:stroke-gray-600 cursor-pointer transition-opacity hover:opacity-80"
                      onMouseEnter={(e) => onDeptEnter(e, feature)}
                      onMouseMove={(e) => onDeptEnter(e, feature)}
                      onMouseLeave={onDeptLeave}
                    />
                  )
                })}
              </g>
            </g>
          </svg>

          {tooltip.visible && (
            <div
              className="pointer-events-none absolute z-10 rounded-md border border-border bg-background px-2 py-1 text-xs shadow-md"
              style={{
                left: tooltip.x + 12,
                top: tooltip.y + 12,
                transform: tooltip.x > (svgRef.current?.getBoundingClientRect().width ?? VIEWBOX_W) - 140
                  ? 'translateX(-100%) translateX(-24px)' : undefined,
              }}
            >
              <div className="font-medium">{tooltip.nombre}</div>
              <div className="text-muted-foreground">{fmt(tooltip.valor)}</div>
            </div>
          )}
        </div>

        <div className="mt-3 flex items-center justify-between gap-2 text-xs text-muted-foreground">
          <span>0</span>
          <div className="flex h-2 flex-1 overflow-hidden rounded-full">
            {activeShades.map((hex, i) => (
              <div key={i} className="flex-1" style={{ background: hex }} />
            ))}
          </div>
          <span>{fmt(max)}</span>
        </div>
      </CardContent>
    </Card>
  )
}
